/**
 * Own add/edit state for a bounded ServiceNow table feed.
 *
 * The hook defaults new connections to workspace-scoped UMD Credential Store
 * keys, preserves masked workspace secrets during edits, and hands a
 * historical first run to the provider-neutral sync workflow.
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import type {
  ConnectorCredentialSource,
  ConnectorFeedFor,
  ConnectorInitialSync,
  ConnectorSchedule,
  ConnectorSelectionPreview,
} from '../../types'
import { sameConnectorSelection } from '../../types'
import {
  createServiceNowFeed,
  deleteServiceNowConnection,
  getServiceNowConnection,
  getServiceNowTableAccess,
  getServiceNowTables,
  previewServiceNowFeed,
  saveServiceNowConnection,
  updateServiceNowFeed,
} from '../services/api'
import type {
  ServiceNowConnectionStatus,
  ServiceNowFilter,
  ServiceNowTable,
  ServiceNowTableAccessCatalog,
  ServiceNowTableDefinitions,
  ServiceNowWorkspaceCredentials,
} from '../types'
import {
  hasCompleteServiceNowCredentials,
  isServiceNowApiUrl,
  SERVICENOW_DEFAULT_API_URL,
  serviceNowFormError,
} from './serviceNowFormValidation'
import { useServiceNowSelectionPreview } from './useServiceNowSelectionPreview'

const EMPTY_CREDENTIALS: ServiceNowWorkspaceCredentials = {
  clientId: '',
  clientSecret: '',
  username: '',
  password: '',
}

export function useServiceNowFeedForm({
  api,
  orgId,
  vaultId,
  feed,
  open,
  canEditQuery,
  canAdministerWorkspace,
  onConfigured,
  onSyncStarted,
}: {
  api: NodeApiClient
  orgId: string
  vaultId: string
  feed?: ConnectorFeedFor<'servicenow'> | null
  open: boolean
  canEditQuery: boolean
  canAdministerWorkspace: boolean
  onConfigured: () => Promise<void>
  onSyncStarted: () => void
}) {
  const [connection, setConnection] = useState<ServiceNowConnectionStatus>({
    workspaceConfigured: null,
    umdConfigured: null,
    umdCredentialKeys: [],
  })
  const [credentialSource, setCredentialSource] =
    useState<ConnectorCredentialSource>('umd')
  const [credentials, setCredentials] =
    useState<ServiceNowWorkspaceCredentials>(EMPTY_CREDENTIALS)
  const [apiUrl, setApiUrl] = useState(SERVICENOW_DEFAULT_API_URL)
  const [table, setTable] = useState<ServiceNowTable>('incident')
  const [recordLimit, setRecordLimit] = useState(100)
  const [filters, setFilters] = useState<ServiceNowFilter[]>([])
  const [sourceDisplayFieldKeys, setSourceDisplayFieldKeys] = useState<string[]>([])
  const [tableDefinitions, setTableDefinitions] =
    useState<ServiceNowTableDefinitions | null>(null)
  const [tableAccess, setTableAccess] =
    useState<ServiceNowTableAccessCatalog | null>(null)
  const [initialSync, setInitialSync] = useState<ConnectorInitialSync>('all')
  const [schedule, setSchedule] = useState<ConnectorSchedule>('manual')
  const [loadingConnection, setLoadingConnection] = useState(false)
  const [loadingTables, setLoadingTables] = useState(false)
  const [loadingTableAccess, setLoadingTableAccess] = useState(false)
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState<ConnectorSelectionPreview>()
  const editing = Boolean(feed)
  const credentialConfigured = credentialSource === 'umd'
    ? connection.umdConfigured === true
    : connection.workspaceConfigured === true
  const selection = useServiceNowSelectionPreview({
    api,
    orgId,
    vaultId,
    open,
    enabled: canEditQuery && credentialConfigured && isServiceNowApiUrl(apiUrl.trim()),
    apiUrl: apiUrl.trim(),
    table,
    credentialSource,
    recordLimit,
    filters,
  })

  useEffect(() => {
    setPreview(undefined)
  }, [apiUrl, credentialSource, credentials, filters, initialSync, recordLimit, schedule, sourceDisplayFieldKeys, table])

  useEffect(() => {
    if (!open) return
    const saved = feed?.servicenow
    const source = saved?.credentialSource ?? 'umd'
    const selectedTable = (feed?.remoteFolderId as ServiceNowTable | undefined) ?? 'incident'
    setCredentialSource(source)
    setCredentials(EMPTY_CREDENTIALS)
    setApiUrl(saved?.apiUrl ?? SERVICENOW_DEFAULT_API_URL)
    setTable(selectedTable)
    setRecordLimit(saved?.recordLimit ?? 100)
    setFilters(saved?.filters ?? [])
    setSourceDisplayFieldKeys(feed ? feed.sourceDisplayFieldKeys : [])
    setInitialSync('all')
    setSchedule(feed?.schedule ?? 'manual')
    setPreview(undefined)
    setTableDefinitions(null)
    setTableAccess(null)
    setLoadingConnection(true)
    void getServiceNowConnection(api, orgId, vaultId)
      .then(setConnection)
      .catch(() => toast.error('Could not read the ServiceNow connection'))
      .finally(() => setLoadingConnection(false))
    setLoadingTables(true)
    void getServiceNowTables(api, orgId, vaultId)
      .then(definitions => {
        setTableDefinitions(definitions)
        if (!saved) {
          const definition = definitions.supportedTables.find(
            candidate => candidate.table === selectedTable,
          )
          setFilters(definition?.defaultFilters ?? [])
          setSourceDisplayFieldKeys(canAdministerWorkspace
            ? definition?.sourceDisplayFields
              .filter(field => field.default)
              .map(field => field.key) ?? []
            : [])
        }
      })
      .catch(() => toast.error('Could not load ServiceNow table options'))
      .finally(() => setLoadingTables(false))
  }, [api, canAdministerWorkspace, feed, open, orgId, vaultId])

  const selectTable = (selected: ServiceNowTable) => {
    setTable(selected)
    const definition = tableDefinitions?.supportedTables.find(
      candidate => candidate.table === selected,
    )
    setFilters(definition?.defaultFilters ?? [])
    setSourceDisplayFieldKeys(canAdministerWorkspace
      ? definition?.sourceDisplayFields
        .filter(field => field.default)
        .map(field => field.key) ?? []
      : [])
  }

  const selectCredentialSource = (source: ConnectorCredentialSource) => {
    setCredentialSource(source)
    setTableAccess(null)
  }

  const loadTableAccess = () => {
    const normalizedUrl = apiUrl.trim()
    if (tableAccess || loadingTableAccess || !isServiceNowApiUrl(normalizedUrl)) return
    setLoadingTableAccess(true)
    void getServiceNowTableAccess(
      api,
      orgId,
      vaultId,
      normalizedUrl,
      credentialSource,
    )
      .then(setTableAccess)
      .catch(() => toast.error('Could not inspect ServiceNow table access'))
      .finally(() => setLoadingTableAccess(false))
  }

  const submit = async () => {
    if (!canEditQuery && feed) {
      setSaving(true)
      try {
        await updateServiceNowFeed(
          api,
          orgId,
          vaultId,
          feed.feedId,
          schedule,
          sourceDisplayFieldKeys,
          {
            apiUrl: apiUrl.trim(),
            credentialSource,
            recordLimit,
            filters,
          },
        )
        toast.success('ServiceNow presentation settings saved.')
        await onConfigured()
      } catch (error: any) {
        toast.error(
          error?.response?.data?.detail
          ?? error?.message
          ?? 'Could not save the ServiceNow presentation settings',
        )
      } finally {
        setSaving(false)
      }
      return
    }
    const validationError = serviceNowFormError({
      apiUrl: apiUrl.trim(),
      recordLimit,
      filters,
      credentialSource,
      connection,
      credentials,
    })
    if (validationError) {
      toast.error(validationError)
      return
    }
    const completeBundle = hasCompleteServiceNowCredentials(credentials)

    setSaving(true)
    try {
      if (credentialSource === 'workspace' && completeBundle) {
        await saveServiceNowConnection(api, orgId, vaultId, credentials)
        setConnection(current => ({ ...current, workspaceConfigured: true }))
        setCredentials(EMPTY_CREDENTIALS)
      }
      const servicenow = {
        apiUrl: apiUrl.trim(),
        credentialSource,
        recordLimit,
        filters,
      }
      const request = {
        table,
        initialSync,
        schedule,
        sourceDisplayFieldKeys,
        servicenow,
      }
      const nextPreview = await previewServiceNowFeed(
        api,
        orgId,
        vaultId,
        request,
      )
      const selectionConfirmed = sameConnectorSelection(preview, nextPreview)
      setPreview(nextPreview)
      if (!selectionConfirmed) return
      const mutation = await (feed
        ? updateServiceNowFeed(
            api,
            orgId,
            vaultId,
            feed.feedId,
            schedule,
            sourceDisplayFieldKeys,
            servicenow,
          )
        : createServiceNowFeed(api, orgId, vaultId, {
            table,
            initialSync,
            schedule,
            sourceDisplayFieldKeys,
            servicenow,
          }))
      if (mutation.jobId) {
        toast.info(
          editing
            ? 'ServiceNow source updated. Sync started.'
            : 'ServiceNow source connected. Sync started.',
        )
        onSyncStarted()
      } else {
        toast.success(
          editing
            ? 'ServiceNow source updated.'
            : 'ServiceNow source connected.',
        )
      }
      void onConfigured().catch(() => {
        toast.error('ServiceNow source was saved, but connected views could not refresh')
      })
    } catch (error: any) {
      const detail = error?.response?.data?.detail
        ?? error?.message
        ?? `ServiceNow ${editing ? 'update' : 'setup'} failed`
      toast.error(detail)
    } finally {
      setSaving(false)
    }
  }

  const deleteCredential = async () => {
    setSaving(true)
    try {
      await deleteServiceNowConnection(api, orgId, vaultId)
      setConnection(current => ({ ...current, workspaceConfigured: false }))
      setCredentials(EMPTY_CREDENTIALS)
      toast.success('ServiceNow workspace credentials deleted')
    } catch (error: any) {
      toast.error(
        error?.response?.data?.detail
        ?? 'Could not delete the ServiceNow workspace credentials',
      )
    } finally {
      setSaving(false)
    }
  }

  return {
    connection,
    credentialSource,
    credentials,
    apiUrl,
    table,
    recordLimit,
    filters,
    sourceDisplayFields: tableDefinitions?.supportedTables.find(
      definition => definition.table === table,
    )?.sourceDisplayFields ?? [],
    sourceDisplayFieldKeys,
    tableDefinitions,
    tableAccess,
    initialSync,
    schedule,
    loadingConnection,
    loadingTables,
    loadingTableAccess,
    saving,
    preview,
    selectionPreview: selection.preview,
    loadingSelectionPreview: selection.loading,
    selectionPreviewError: selection.error,
    editing,
    setCredentialSource: selectCredentialSource,
    setCredentials,
    setApiUrl: (value: string) => {
      setApiUrl(value)
      setTableAccess(null)
    },
    setTable: selectTable,
    setRecordLimit,
    setFilters,
    setSourceDisplayFieldKeys,
    setInitialSync,
    setSchedule,
    loadTableAccess,
    submit,
    deleteCredential,
  }
}
